/**
 * Item panel filter helpers for Work Panel Items.
 */

export const DEFAULT_ITEM_PANEL_FILTERS = {
    activity_days: null,
    assignment_days: null,
    assignee_ids: [],
    include_unassigned: false,
};

export const UNASSIGNED_ASSIGNMENT_STATUSES = [
    "end_assigned_pre_completion",
];

const STORAGE_KEY_PREFIX = "kaamfu_item_panel_filters_";

/**
 * @param {object|null|undefined} item
 * @param {{
 *   activity_days: number|null,
 *   assignment_days: number|null,
 *   assignee_ids: Array<number|string>,
 *   include_unassigned?: boolean,
 * }} filters
 * @returns {boolean}
 */
export function itemMatchesItemPanelFilters(item, filters) {
    if (!item || !filters) {
        return true;
    }

    const {
        activity_days,
        assignment_days,
        assignee_ids,
        include_unassigned,
    } = filters;

    if (!matchesActivityFilter(item, activity_days)) {
        return false;
    }

    if (!matchesAssignmentFilter(item, assignment_days)) {
        return false;
    }

    if (!matchesAssigneeFilter(item, assignee_ids, include_unassigned)) {
        return false;
    }

    return true;
}

export function hasActiveItemPanelFilters(filters) {
    if (!filters) {
        return false;
    }

    return (
        filters.activity_days !== null ||
        filters.assignment_days !== null ||
        (filters.assignee_ids?.length ?? 0) > 0 ||
        Boolean(filters.include_unassigned)
    );
}

/**
 * Locate Item must reveal the target row. Keep active Work Filters when the
 * located item is already included in the filtered results; clear only when
 * filters would hide it (or when the item cannot be evaluated).
 *
 * @param {object|null|undefined} filters
 * @param {() => void} clearFilters
 * @param {{ item?: object|null }} [options]
 * @returns {boolean} whether filters were cleared
 */
export function clearItemPanelFiltersForLocate(
    filters,
    clearFilters,
    options = {},
) {
    if (!hasActiveItemPanelFilters(filters)) {
        return false;
    }

    const { item = null } = options ?? {};

    // Item already visible under the active Work Filter — leave it alone.
    if (item && itemMatchesItemPanelFilters(item, filters)) {
        return false;
    }

    clearFilters?.();

    return true;
}

/**
 * Load the destination board's Work Filters into memory, then clear only when
 * the located item would stay hidden under those filters.
 *
 * In-memory `item_panel_filters` can still belong to the previous board (or
 * defaults) until the board watcher runs on nextTick. Deciding from that stale
 * state skips clearing when the earlier board had no filters, then destination
 * filters reload afterward and hide the located item.
 *
 * @param {{
 *   boardId: number|string|null|undefined,
 *   loadFiltersForBoard: (boardId: number|string|null|undefined) => void,
 *   getFilters: () => object|null|undefined,
 *   clearFilters: () => void,
 *   item?: object|null,
 * }} options
 * @returns {boolean} whether filters were cleared
 */
export function clearDestinationItemPanelFiltersForLocate({
    boardId,
    loadFiltersForBoard,
    getFilters,
    clearFilters,
    item = null,
}) {
    if (boardId != null && typeof loadFiltersForBoard === "function") {
        loadFiltersForBoard(boardId);
    }

    const filters =
        typeof getFilters === "function" ? getFilters() : getFilters;

    return clearItemPanelFiltersForLocate(filters, clearFilters, { item });
}

export function isItemUnassigned(item) {
    if (!item?.version) {
        return true;
    }

    return UNASSIGNED_ASSIGNMENT_STATUSES.includes(
        item.version.current_assignment_status,
    );
}

function matchesActivityFilter(item, activity_days) {
    if (activity_days === null || activity_days === undefined) {
        return true;
    }

    return isWithinLastDays(item.last_activity_at, activity_days);
}

function matchesAssignmentFilter(item, assignment_days) {
    if (assignment_days === null || assignment_days === undefined) {
        return true;
    }

    const assigned_at = item?.version?.assigned_at;

    if (!assigned_at) {
        return false;
    }

    return isWithinLastDays(assigned_at, assignment_days);
}

function matchesAssigneeFilter(item, assignee_ids, include_unassigned = false) {
    const has_assignee_ids = (assignee_ids?.length ?? 0) > 0;

    if (!has_assignee_ids && !include_unassigned) {
        return true;
    }

    if (include_unassigned && isItemUnassigned(item)) {
        return true;
    }

    if (!has_assignee_ids) {
        return false;
    }

    const version = item?.version;

    if (!version) {
        return false;
    }

    const current_assignee_id = version.user_profile_id_assignee;
    const last_assignee_id =
        version.current_assignment_status === "end_assigned_by_completion"
            ? version.last_assignee_id
            : null;

    return assignee_ids.some(
        (profile_id) =>
            profile_id == current_assignee_id || profile_id == last_assignee_id,
    );
}

function isWithinLastDays(date_value, days) {
    const date = parseUtcDate(date_value);

    if (!date) {
        return false;
    }

    const threshold_ms = Date.now() - days * 24 * 60 * 60 * 1000;

    return date.getTime() >= threshold_ms;
}

/**
 * Backend timestamps are UTC. ISO strings with Z/offset parse correctly;
 * timezone-less MySQL-style strings must be treated as UTC (not local).
 */
function parseUtcDate(date_value) {
    if (!date_value) {
        return null;
    }

    if (date_value instanceof Date) {
        return Number.isNaN(date_value.getTime()) ? null : date_value;
    }

    if (typeof date_value === "number") {
        const date = new Date(date_value);

        return Number.isNaN(date.getTime()) ? null : date;
    }

    const raw = String(date_value).trim();

    if (!raw) {
        return null;
    }

    const has_timezone = /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(raw);
    let normalized = raw;

    if (!has_timezone) {
        if (raw.includes(" ")) {
            normalized = `${raw.replace(" ", "T")}Z`;
        } else if (raw.includes("T")) {
            normalized = `${raw}Z`;
        }
    }

    const date = new Date(normalized);

    return Number.isNaN(date.getTime()) ? null : date;
}

export function getDefaultItemPanelFilters() {
    return {
        activity_days: null,
        assignment_days: null,
        assignee_ids: [],
        include_unassigned: false,
    };
}

export function getItemPanelFiltersStorageKey(user_profile_id) {
    return `${STORAGE_KEY_PREFIX}${user_profile_id}`;
}

export function readItemPanelFiltersMap(user_profile_id) {
    if (!user_profile_id) {
        return {};
    }

    try {
        const raw = localStorage.getItem(
            getItemPanelFiltersStorageKey(user_profile_id),
        );

        if (!raw) {
            return {};
        }

        const parsed = JSON.parse(raw);

        return parsed && typeof parsed === "object" ? parsed : {};
    } catch (error) {
        return {};
    }
}

export function writeItemPanelFiltersMap(user_profile_id, filters_map) {
    if (!user_profile_id) {
        return;
    }

    localStorage.setItem(
        getItemPanelFiltersStorageKey(user_profile_id),
        JSON.stringify(filters_map ?? {}),
    );
}

export function loadItemPanelFiltersFromStorage(user_profile_id, board_id) {
    if (!user_profile_id || !board_id) {
        return getDefaultItemPanelFilters();
    }

    const filters_map = readItemPanelFiltersMap(user_profile_id);
    const stored_filters = filters_map[String(board_id)];

    return normalizeItemPanelFilters(stored_filters);
}

export function saveItemPanelFiltersToStorage(
    user_profile_id,
    board_id,
    filters,
) {
    if (!user_profile_id || !board_id) {
        return;
    }

    const filters_map = readItemPanelFiltersMap(user_profile_id);

    filters_map[String(board_id)] = normalizeItemPanelFilters(filters);
    writeItemPanelFiltersMap(user_profile_id, filters_map);
}

/**
 * Keep only assignee ids that still belong to the filter profile set and are not agents.
 * When profile_ids is empty, skip pruning (filter profiles not loaded yet).
 *
 * @param {object} filters
 * @param {Array<number|string>} profile_ids
 * @param {Array<{id: number|string, is_agent?: boolean}>} profiles
 * @returns {{ filters: object, did_change: boolean }}
 */
export function sanitizeItemPanelFilters(
    filters,
    profile_ids = [],
    profiles = [],
) {
    const normalized_filters = normalizeItemPanelFilters(filters);

    if (!profile_ids?.length) {
        return {
            filters: normalized_filters,
            did_change: false,
        };
    }

    const agent_id_set = new Set(
        (profiles ?? [])
            .filter((profile) => profile?.is_agent)
            .map((profile) => profile.id),
    );

    const sanitized_assignee_ids = normalized_filters.assignee_ids.filter(
        (profile_id) => {
            const belongs_to_board = profile_ids.some(
                (board_profile_id) => board_profile_id == profile_id,
            );
            const is_agent = [...agent_id_set].some(
                (agent_id) => agent_id == profile_id,
            );

            return belongs_to_board && !is_agent;
        },
    );

    const did_change =
        sanitized_assignee_ids.length !==
            normalized_filters.assignee_ids.length ||
        sanitized_assignee_ids.some(
            (profile_id, index) =>
                profile_id != normalized_filters.assignee_ids[index],
        );

    return {
        filters: {
            ...normalized_filters,
            assignee_ids: sanitized_assignee_ids,
            include_unassigned: normalized_filters.include_unassigned,
        },
        did_change,
    };
}

/**
 * Rebuild a full item list after a filtered drag-and-drop reorder.
 * Keeps filtered-out items anchored before the next visible item
 * instead of appending them at the end.
 *
 * Example: [U1, A1, U2] + drop U3 between U1 and U2 → [U1, U3, A1, U2]
 *
 * @param {Array<object>} original_items
 * @param {Array<object>} new_visible_items
 * @param {(item: object) => boolean} matches_filter
 * @returns {Array<object>}
 */
export function mergeVisibleItemOrderIntoFullList(
    original_items = [],
    new_visible_items = [],
    matches_filter,
) {
    if (typeof matches_filter !== "function") {
        return [...(new_visible_items ?? [])];
    }

    const before_hidden_by_visible_id = new Map();
    const pending_hidden_items = [];
    const trailing_hidden_items = [];

    for (const item of original_items ?? []) {
        if (!item?.id) {
            continue;
        }

        if (matches_filter(item)) {
            before_hidden_by_visible_id.set(item.id, [...pending_hidden_items]);
            pending_hidden_items.length = 0;
            continue;
        }

        pending_hidden_items.push(item);
    }

    trailing_hidden_items.push(...pending_hidden_items);

    const merged_items = [];

    for (const visible_item of new_visible_items ?? []) {
        if (!visible_item?.id) {
            continue;
        }

        const leading_hidden_items =
            before_hidden_by_visible_id.get(visible_item.id) ?? [];
        if (leading_hidden_items.length) {
            merged_items.push(...leading_hidden_items);
            before_hidden_by_visible_id.delete(visible_item.id);
        }

        merged_items.push(visible_item);
    }

    // Hidden items whose following visible item left the category.
    for (const orphaned_hidden_items of before_hidden_by_visible_id.values()) {
        merged_items.push(...orphaned_hidden_items);
    }

    merged_items.push(...trailing_hidden_items);

    return merged_items;
}

function normalizeItemPanelFilters(filters) {
    if (!filters || typeof filters !== "object") {
        return getDefaultItemPanelFilters();
    }

    const activity_days =
        filters.activity_days === null || filters.activity_days === undefined
            ? null
            : Number(filters.activity_days);

    const assignment_days =
        filters.assignment_days === null ||
        filters.assignment_days === undefined
            ? null
            : Number(filters.assignment_days);

    return {
        activity_days: Number.isFinite(activity_days) ? activity_days : null,
        assignment_days: Number.isFinite(assignment_days)
            ? assignment_days
            : null,
        assignee_ids: Array.isArray(filters.assignee_ids)
            ? [...filters.assignee_ids]
            : [],
        include_unassigned: Boolean(filters.include_unassigned),
    };
}

/**
 * When the active board changes while the filter menu is open, start a new
 * assignees refresh. An in-flight request for the previous board exits early
 * and will not mark ready, so without a refresh the spinner can stick.
 *
 * @param {boolean} is_menu_open
 * @returns {boolean}
 */
export function shouldRefreshAssigneesOnActiveBoardChange(is_menu_open) {
    return Boolean(is_menu_open);
}

/**
 * Only the fetch for the currently active board may clear the assignees loader.
 *
 * @param {number|string|null|undefined} request_board_id
 * @param {number|string|null|undefined} active_board_id
 * @returns {boolean}
 */
export function shouldMarkAssigneesReadyAfterFetch(
    request_board_id,
    active_board_id,
) {
    return request_board_id == active_board_id;
}
